import React from 'react';
import { Link } from 'react-router-dom';
import { Ticket, Disc3, Building2, Shield, ArrowRight } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useDashboardTarget } from '@/hooks/useDashboardTarget';
import api from '@/services/api';

// Platform capabilities, framed by audience: Attendees / DJs / Organizers / Admins.
// Informational showcase — each tile links to a real, existing surface. The Admin
// tile only exposes a link to actual admins (resolved from /users/me, same as Navbar).
export function PlatformFeatures() {
  const { isAuthenticated } = useAuth();
  const [isAdmin, setIsAdmin] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    if (isAuthenticated && api.isAuthenticated()) {
      api.get('/users/me')
        .then((me) => { if (active) setIsAdmin(me?.role === 'admin'); })
        .catch(() => { if (active) setIsAdmin(false); });
    } else {
      setIsAdmin(false);
    }
    return () => { active = false; };
  }, [isAuthenticated]);

  const tiles = [
    {
      label: 'Attendees',
      icon: Ticket,
      points: ['Events & ticketing', 'My Events / My Tickets', 'User accounts'],
      cta: 'Explore events',
      to: '/events',
    },
    {
      label: 'DJs',
      icon: Disc3,
      points: ['DJ profiles & directory', 'DJ approval flow', 'Showcase your sets'],
      cta: 'Meet the DJs',
      to: '/djs',
    },
    {
      label: 'Organizers',
      icon: Building2,
      points: ['Organizer tools', 'Event management', 'Publish & feature events'],
      cta: isAuthenticated ? 'Manage events' : 'Start organizing',
      to: isAuthenticated ? '/organizer/events' : '/login',
    },
    {
      label: 'Admins',
      icon: Shield,
      points: ['Admin dashboard', 'Users & organizers', 'Full platform control'],
      cta: isAdmin ? 'Open admin' : null,
      to: isAdmin ? '/admin' : null,
    },
  ];

  return (
    <section className="py-16 animate-fade-in">
      <div className="container mx-auto px-4">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl font-display font-bold mb-2">
            Everything <span className="text-gradient-gold">SoundWolves</span> does
          </h2>
          <p className="text-muted-foreground">One platform for attendees, DJs, organizers, and admins</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiles.map((t) => {
            const Icon = t.icon;
            return (
              <div key={t.label} className="glass-card hover-lift rounded-xl p-6 border border-border/50 flex flex-col">
                <div className="w-11 h-11 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <h3 className="font-display font-bold text-xl mb-3">{t.label}</h3>
                <ul className="space-y-2 flex-1 mb-4">
                  {t.points.map((p) => (
                    <li key={p} className="text-sm text-muted-foreground flex items-start gap-2">
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
                {t.to && (
                  <Link to={t.to} className="group inline-flex items-center text-sm font-medium text-primary">
                    {t.cta}
                    <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default PlatformFeatures;
